import {
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import type {
  ReportChange,
  ReportDefinition,
  ReportInput,
  ReportSnapshot,
  ReportTemplate,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime, formatRange } from "../format.ts";
import { pathTo, scope, siteTree } from "../context.ts";
import { Icon } from "../icons.tsx";
import { kpiText, toReportCsv, toReportSheets } from "../reports.ts";
import { buildXlsx } from "../xlsx.ts";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { ReportPaper, reportFormatters } from "../components/ReportPaper.tsx";
import {
  inputOf,
  newReport,
  ReportWizard,
} from "../components/ReportWizard.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const TABS = ["defined", "wizard", "preview", "history"] as const;
type Tab = (typeof TABS)[number];

/**
 * Reports (prototype "Raporty"): defined reports with their schedule and recipients, the wizard, the preview of a
 * snapshot and the history of all snapshots. A snapshot never changes; whether today's data differs is checked on
 * request and shown next to it.
 */
export function ReportsPage() {
  const [params, setParams] = useSearchParams<{
    tab?: string;
    snapshot?: string;
  }>();
  const tab = (): Tab =>
    TABS.includes(params.tab as Tab) ? (params.tab as Tab) : "defined";
  const setTab = (next: Tab) => setParams({ tab: next });
  const [templates] = createResource(() => api.reports.templates());
  const [reports, { refetch: refetchReports }] = createResource(() =>
    api.reports.list(),
  );
  const [snapshots, { refetch: refetchSnapshots }] = createResource(() =>
    api.reports.snapshots(),
  );
  const [users] = createResource(() => api.events.users());
  const [editing, setEditing] = createSignal<{
    report?: ReportDefinition;
    initial?: ReportInput;
  }>({});
  const [generating, setGenerating] = createSignal<ReportDefinition>();
  const [removing, setRemoving] = createSignal<ReportDefinition>();

  const reload = () => {
    api.invalidate("/api/v1/reports");
    api.invalidate("/api/v1/report-snapshots");
    void refetchReports();
    void refetchSnapshots();
  };
  const reportOf = (id: string) => reports()?.find((r) => r.id === id);
  const lastOf = (id: string) => snapshots()?.find((s) => s.reportId === id);
  const openSnapshot = (id: string) =>
    setParams({ tab: "preview", snapshot: id });
  const startWizard = (w: {
    report?: ReportDefinition;
    initial?: ReportInput;
  }) => {
    setEditing(w);
    setTab("wizard");
  };
  const generate = (r: ReportDefinition) => {
    if (r.askComment || r.period === "on-generate") {
      setGenerating(r);
    } else {
      void api.reports.generate(r.id).then((s) => {
        reload();
        openSnapshot(s.id);
      });
    }
  };
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? "";
  const userName = (id: string) =>
    users()?.find((u) => u.id === id)?.name ?? id;

  return (
    <>
      <PageHeader
        eyebrow={`${t("reports.eyebrow")} · ${scope()?.name ?? ""}`}
        title={t(`reports.title.${tab()}`)}
        sub={t(`reports.intro.${tab()}`)}
      >
        <Show when={tab() === "defined" && operator()}>
          <button
            type="button"
            class="btn primary"
            onClick={() => startWizard({})}
          >
            <Icon name="plus" />
            {t("reports.new")}
          </button>
        </Show>
      </PageHeader>
      <Tabs
        tabs={TABS}
        value={tab()}
        onChange={(x) => {
          if (x === "wizard") {
            setEditing({});
          }
          setTab(x);
        }}
        label={(x) => t(`reports.tab.${x}`)}
      />
      <Show when={!reports.error} fallback={<ErrorBox error={reports.error} />}>
        <Show when={tab() === "defined"}>
          <Defined
            reports={reports() ?? []}
            templates={templates() ?? []}
            loading={reports.loading}
            lastOf={lastOf}
            nodeName={nodeName}
            userName={userName}
            onGenerate={generate}
            onOpen={openSnapshot}
            onEdit={(r) => startWizard({ report: r })}
            onDuplicate={(r) =>
              startWizard({
                initial: {
                  ...inputOf(r),
                  name: t("reports.copyOf", { name: r.name }),
                },
              })
            }
            onRemove={setRemoving}
            onTemplate={(x) =>
              startWizard({
                initial: newReport(
                  x,
                  scope()?.id ?? siteTree()?.nodes[0]?.id ?? "",
                ),
              })
            }
          />
        </Show>
        <Show when={tab() === "wizard"}>
          <Show
            when={operator()}
            fallback={
              <section class="card empty">
                <p class="muted">{t("reports.wizard.noRights")}</p>
              </section>
            }
          >
            <Show
              when={templates()}
              fallback={
                <section class="card">
                  <Skeleton lines={6} />
                </section>
              }
            >
              {(list) => (
                <Show when={editing()} keyed>
                  {(w) => (
                    <ReportWizard
                      templates={list()}
                      report={w.report}
                      initial={w.initial}
                      onCancel={() => setTab("defined")}
                      onSaved={(_, snapshot) => {
                        reload();
                        if (snapshot) {
                          openSnapshot(snapshot.id);
                        } else {
                          setTab("defined");
                        }
                      }}
                    />
                  )}
                </Show>
              )}
            </Show>
          </Show>
        </Show>
        <Show when={tab() === "preview"}>
          <Preview
            snapshotId={
              params.snapshot ?? snapshots()?.find((s) => s.status === "ok")?.id
            }
            reportOf={reportOf}
            onGenerate={generate}
            onGenerated={(s) => {
              reload();
              openSnapshot(s.id);
            }}
          />
        </Show>
        <Show when={tab() === "history"}>
          <History
            snapshots={snapshots() ?? []}
            reports={reports() ?? []}
            reportOf={reportOf}
            nodeName={nodeName}
            onOpen={openSnapshot}
          />
        </Show>
      </Show>
      <Show when={generating()}>
        {(r) => (
          <GenerateDialog
            report={r()}
            onClose={() => setGenerating(undefined)}
            onDone={(s) => {
              reload();
              openSnapshot(s.id);
            }}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(r) => (
          <ConfirmDialog
            title={t("reports.removeTitle", { name: r().name })}
            text={t("reports.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.reports.remove(r().id);
              reload();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}

function StatusChip(props: { snapshot: ReportSnapshot }) {
  return (
    <span class={`chip ${props.snapshot.status === "ok" ? "ok" : "crit"}`}>
      {t(`reports.status.${props.snapshot.status}`)}
    </span>
  );
}

function Defined(props: {
  reports: ReportDefinition[];
  templates: ReportTemplate[];
  loading: boolean;
  lastOf: (id: string) => ReportSnapshot | undefined;
  nodeName: (id: string) => string;
  userName: (id: string) => string;
  onGenerate: (r: ReportDefinition) => void;
  onOpen: (snapshotId: string) => void;
  onEdit: (r: ReportDefinition) => void;
  onDuplicate: (r: ReportDefinition) => void;
  onRemove: (r: ReportDefinition) => void;
  onTemplate: (x: ReportTemplate) => void;
}) {
  return (
    <>
      <section class="card">
        <Show
          when={!props.loading || props.reports.length > 0}
          fallback={<Skeleton lines={4} />}
        >
          <Show
            when={props.reports.length > 0}
            fallback={
              <div class="card-b">
                <p class="muted" style={{ margin: 0 }}>
                  {t("reports.none")}
                </p>
              </div>
            }
          >
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("reports.th.report")}</th>
                    <th>{t("reports.th.scope")}</th>
                    <th>{t("reports.th.schedule")}</th>
                    <th>{t("reports.th.recipients")}</th>
                    <th>{t("reports.th.last")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={props.reports}>
                    {(r) => {
                      const last = () => props.lastOf(r.id);
                      return (
                        <tr>
                          <td>
                            <b style={{ "font-weight": 500 }}>{r.name}</b>
                            <div class="hint">
                              {t("reports.templateOf", {
                                name: t(`reports.template.${r.template}.name`),
                              })}{" "}
                              ·{" "}
                              {r.formats.map((x) => x.toUpperCase()).join(", ")}
                            </div>
                          </td>
                          <td>
                            {props.nodeName(r.nodeId)}
                            <div class="hint">
                              {t(`reports.period.${r.period}`)}
                            </div>
                          </td>
                          <td>{t(`reports.schedule.${r.schedule}`)}</td>
                          <td>
                            {r.recipients.length > 0 ? (
                              r.recipients.map(props.userName).join(", ")
                            ) : (
                              <span class="hint">—</span>
                            )}
                          </td>
                          <td>
                            <Show
                              when={last()}
                              fallback={
                                <span class="hint">{t("reports.notYet")}</span>
                              }
                            >
                              {(s) => (
                                <>
                                  <span class="num">
                                    {formatDateTime(s().generatedAt, locale())}
                                  </span>
                                  <div>
                                    <StatusChip snapshot={s()} />
                                  </div>
                                </>
                              )}
                            </Show>
                          </td>
                          <td>
                            {/* One row, centred: buttons with and without an icon differ in height. */}
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                "align-items": "center",
                                "justify-content": "flex-end",
                                "white-space": "nowrap",
                              }}
                            >
                              <button
                                type="button"
                                class="btn sm"
                                onClick={() => props.onGenerate(r)}
                              >
                                <Icon name="file" />
                                {t("reports.generate")}
                              </button>
                              <Show when={last()?.status === "ok"}>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  onClick={() => props.onOpen(last()!.id)}
                                >
                                  {t("reports.tab.preview")}
                                </button>
                              </Show>
                              <Show when={operator()}>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  title={t("common.edit")}
                                  aria-label={t("common.edit")}
                                  onClick={() => props.onEdit(r)}
                                >
                                  <Icon name="edit" />
                                </button>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  title={t("reports.duplicate")}
                                  aria-label={t("reports.duplicate")}
                                  onClick={() => props.onDuplicate(r)}
                                >
                                  <Icon name="plus" />
                                </button>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  title={t("common.delete")}
                                  aria-label={t("common.delete")}
                                  onClick={() => props.onRemove(r)}
                                >
                                  <Icon name="x" />
                                </button>
                              </Show>
                            </div>
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Show>
        <div class="card-b hint">{t("reports.definedHint")}</div>
      </section>
      <section class="card">
        <div class="card-h">
          <h2>{t("reports.templates")}</h2>
          <span class="meta">
            {t("reports.templatesMeta", {
              ready: props.templates.filter((x) => x.ready).length,
              later: props.templates.filter((x) => !x.ready).length,
            })}
          </span>
        </div>
        <div class="card-b">
          <div class="cards">
            <For each={props.templates}>
              {(x) => (
                <button
                  type="button"
                  class="rc"
                  disabled={!x.ready || !operator()}
                  style={x.ready ? undefined : { opacity: 0.6 }}
                  onClick={() => props.onTemplate(x)}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      "align-items": "center",
                      "justify-content": "space-between",
                    }}
                  >
                    <h3>{t(`reports.template.${x.key}.name`)}</h3>
                    <span class={`chip ${x.ready ? "ok" : ""}`}>
                      {x.ready ? t("reports.ready") : t("reports.later")}
                    </span>
                  </div>
                  <p>
                    <b style={{ "font-weight": 500, color: "var(--ink)" }}>
                      {t("reports.for")}
                    </b>{" "}
                    {t(`reports.template.${x.key}.audience`)}
                  </p>
                  <p>{t(`reports.template.${x.key}.content`)}</p>
                  <p class="hint">
                    {x.ready
                      ? "PDF, CSV"
                      : t(`reports.template.${x.key}.needs`)}
                  </p>
                </button>
              )}
            </For>
          </div>
        </div>
      </section>
    </>
  );
}

function Preview(props: {
  snapshotId: string | undefined;
  reportOf: (id: string) => ReportDefinition | undefined;
  onGenerate: (r: ReportDefinition) => void;
  onGenerated: (s: ReportSnapshot) => void;
}) {
  const [snapshot] = createResource(
    () => props.snapshotId,
    (id) => api.reports.snapshot(id),
  );
  const [changes, setChanges] = createSignal<ReportChange[]>();
  const [showChanges, setShowChanges] = createSignal(false);
  const [check, checkError, checking] = useAction();
  const [again, againError, regenerating] = useAction();
  const report = () =>
    snapshot() ? props.reportOf(snapshot()!.reportId) : undefined;
  const organisation = () =>
    siteTree()?.nodes.find((n) => n.parentId === null)?.name ?? "";
  const period = (s: ReportSnapshot) =>
    formatRange(new Date(s.from), new Date(s.to), locale());

  // A landscape report prints on a landscape page; the rule exists only while this preview is shown.
  const page = document.createElement("style");
  document.head.append(page);
  onCleanup(() => page.remove());
  const print = () => {
    page.textContent = `@page { size: A4 ${report()?.landscape ? "landscape" : "portrait"}; margin: 12mm; }`;
    window.print();
  };
  const csv = () => {
    const s = snapshot()!;
    const r = report();
    const text = toReportCsv(
      {
        name: r?.name ?? "",
        period: period(s),
        version: s.version,
        generated: formatDateTime(s.generatedAt, locale()),
      },
      s.content!,
      reportFormatters(),
    );
    download(new Blob([text], { type: "text/csv" }), "csv");
  };
  const xlsx = () => {
    const s = snapshot()!;
    const r = report();
    const sheets = toReportSheets(
      {
        name: r?.name ?? "",
        period: period(s),
        version: s.version,
        generated: formatDateTime(s.generatedAt, locale()),
      },
      s.content!,
      reportFormatters(),
    );
    download(
      new Blob([buildXlsx(sheets)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "xlsx",
    );
  };
  const download = (blob: Blob, extension: string) => {
    const s = snapshot()!;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(report()?.name ?? "raport").replace(/[\\/:*?"<>|]/g, "")} ${s.from.slice(0, 10)}.${extension}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <Show
      when={props.snapshotId}
      fallback={
        <section class="card empty">
          <p class="muted">{t("reports.noSnapshot")}</p>
        </section>
      }
    >
      <Show
        when={!snapshot.error}
        fallback={<ErrorBox error={snapshot.error} />}
      >
        <Show
          when={snapshot()}
          fallback={
            <section class="card">
              <Skeleton lines={8} />
            </section>
          }
        >
          {(s) => (
            <>
              <section class="card noprint">
                <div class="card-b" style={{ display: "grid", gap: "8px" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      "flex-wrap": "wrap",
                      "align-items": "center",
                    }}
                  >
                    <div
                      style={{ "min-width": 0, "overflow-wrap": "anywhere" }}
                    >
                      <b style={{ "font-weight": 600 }}>{report()?.name}</b>
                      <div class="hint">
                        {period(s())} · {s().content?.nodeName ?? ""} ·{" "}
                        {t("reports.generatedBy", {
                          at: formatDateTime(s().generatedAt, locale()),
                          by:
                            s().generatedBy === "schedule"
                              ? t("reports.bySchedule")
                              : s().generatedBy,
                        })}{" "}
                        · <span class="mono">{s().version}</span>
                      </div>
                    </div>
                    <span
                      class="repbar"
                      style={{
                        "margin-left": "auto",
                        display: "flex",
                        gap: "6px",
                        "flex-wrap": "wrap",
                      }}
                    >
                      <Show when={s().content}>
                        <button type="button" class="btn" onClick={print}>
                          <Icon name="file" />
                          {t("reports.print")}
                        </button>
                        <button type="button" class="btn" onClick={csv}>
                          <Icon name="file" />
                          CSV
                        </button>
                        <button type="button" class="btn" onClick={xlsx}>
                          <Icon name="file" />
                          XLSX
                        </button>
                        <button
                          type="button"
                          class="btn"
                          disabled={checking()}
                          onClick={() =>
                            void check(async () => {
                              setChanges(await api.reports.changes(s().id));
                              setShowChanges(true);
                            })
                          }
                        >
                          {t("reports.check")}
                        </button>
                      </Show>
                      <Show when={report()}>
                        {(r) => (
                          <button
                            type="button"
                            class="btn primary"
                            disabled={regenerating()}
                            onClick={() =>
                              r().askComment
                                ? props.onGenerate(r())
                                : void again(async () => {
                                    const n = await api.reports.generate(
                                      r().id,
                                      {
                                        from: new Date(s().from),
                                        until: new Date(s().to),
                                      },
                                    );
                                    props.onGenerated(n);
                                  })
                            }
                          >
                            {t("reports.again")}
                          </button>
                        )}
                      </Show>
                    </span>
                  </div>
                  <Show when={checkError() ?? againError()}>
                    <ErrorBox error={checkError() ?? againError()} />
                  </Show>
                  <Show when={changes()}>
                    {(c) => (
                      <div
                        class={`banner ${c().length > 0 ? "hist" : ""}`}
                        style={{
                          margin: 0,
                          "flex-wrap": "wrap",
                          "min-width": 0,
                        }}
                      >
                        <Icon name={c().length > 0 ? "alert" : "check"} />
                        <span>
                          <b style={{ "font-weight": 600 }}>
                            {c().length > 0
                              ? t("reports.changed")
                              : t("reports.unchanged")}
                          </b>{" "}
                          {c().length > 0 ? t("reports.changedHint") : ""}
                        </span>
                        <Show when={c().length > 0}>
                          <button
                            type="button"
                            class="btn ghost"
                            onClick={() => setShowChanges(!showChanges())}
                          >
                            {showChanges()
                              ? t("reports.hideChanges")
                              : t("reports.showChanges")}
                          </button>
                        </Show>
                      </div>
                    )}
                  </Show>
                </div>
                <Show when={showChanges() && (changes()?.length ?? 0) > 0}>
                  <div class="scrollx">
                    <table class="tbl">
                      <thead>
                        <tr>
                          <th>{t("reports.th.value")}</th>
                          <th class="r">{t("reports.th.then")}</th>
                          <th class="r">{t("reports.th.now")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={changes()}>
                          {(c) => (
                            <tr>
                              <td>
                                {t(
                                  c.section === "footer"
                                    ? "reports.kpi.completeness"
                                    : `reports.kpi.${c.key}`,
                                )}
                              </td>
                              <td class="r num">
                                {kpiText(
                                  { value: c.then, unit: c.unit },
                                  reportFormatters(),
                                )}
                              </td>
                              <td class="r num">
                                {kpiText(
                                  { value: c.now, unit: c.unit },
                                  reportFormatters(),
                                )}
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </section>
              <Show
                when={s().content}
                fallback={
                  <section class="card empty">
                    <p class="muted">
                      {t("reports.failed", { error: s().error ?? "" })}
                    </p>
                  </section>
                }
              >
                {(content) => (
                  <div class="paperwrap">
                    <ReportPaper
                      name={report()?.name ?? ""}
                      organisation={organisation()}
                      from={s().from}
                      to={s().to}
                      content={content()}
                      generatedAt={s().generatedAt}
                      version={s().version}
                      by={s().generatedBy}
                      comment={s().comment}
                    />
                  </div>
                )}
              </Show>
            </>
          )}
        </Show>
      </Show>
    </Show>
  );
}

function History(props: {
  snapshots: ReportSnapshot[];
  reports: ReportDefinition[];
  reportOf: (id: string) => ReportDefinition | undefined;
  nodeName: (id: string) => string;
  onOpen: (id: string) => void;
}) {
  const [report, setReport] = createSignal("all");
  const [status, setStatus] = createSignal("all");
  const shown = createMemo(() =>
    props.snapshots.filter(
      (s) =>
        (report() === "all" || s.reportId === report()) &&
        (status() === "all" || s.status === status()),
    ),
  );
  return (
    <section class="card">
      <div class="filters" style={{ "flex-wrap": "wrap", gap: "8px" }}>
        <label
          class="field"
          style={{ display: "flex", gap: "6px", "align-items": "center" }}
        >
          <span class="hint">{t("reports.th.report")}</span>
          <select
            value={report()}
            onChange={(e) => setReport(e.currentTarget.value)}
          >
            <option value="all">{t("reports.all")}</option>
            <For each={props.reports}>
              {(r) => <option value={r.id}>{r.name}</option>}
            </For>
          </select>
        </label>
        <label
          class="field"
          style={{ display: "flex", gap: "6px", "align-items": "center" }}
        >
          <span class="hint">{t("reports.th.status")}</span>
          <select
            value={status()}
            onChange={(e) => setStatus(e.currentTarget.value)}
          >
            <option value="all">{t("reports.all")}</option>
            <option value="ok">{t("reports.status.ok")}</option>
            <option value="error">{t("reports.status.error")}</option>
          </select>
        </label>
        <span
          class="hint"
          style={{ "margin-left": "auto", "align-self": "center" }}
        >
          {t("reports.snapshotCount", { count: shown().length })}
        </span>
      </div>
      <Show
        when={shown().length > 0}
        fallback={
          <div class="card-b">
            <p class="muted" style={{ margin: 0 }}>
              {t("reports.noSnapshot")}
            </p>
          </div>
        }
      >
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("reports.th.report")}</th>
                <th>{t("reports.th.period")}</th>
                <th>{t("reports.th.generated")}</th>
                <th>{t("reports.th.version")}</th>
                <th>{t("reports.th.status")}</th>
                <th>{t("reports.th.files")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={shown()}>
                {(s) => {
                  const r = () => props.reportOf(s.reportId);
                  return (
                    <tr
                      class={s.status === "ok" ? "click" : ""}
                      onClick={() => s.status === "ok" && props.onOpen(s.id)}
                    >
                      <td>
                        <b style={{ "font-weight": 500 }}>{r()?.name ?? ""}</b>
                        <div class="hint">
                          {props.nodeName(r()?.nodeId ?? "")} ·{" "}
                          {s.generatedBy === "schedule"
                            ? t("reports.bySchedule")
                            : s.generatedBy}
                        </div>
                      </td>
                      <td>
                        {formatRange(
                          new Date(s.from),
                          new Date(s.to),
                          locale(),
                        )}
                      </td>
                      <td class="num">
                        {formatDateTime(s.generatedAt, locale())}
                      </td>
                      <td class="mono" style={{ "font-size": "11.5px" }}>
                        {s.version}
                      </td>
                      <td>
                        <StatusChip snapshot={s} />
                        <Show when={s.error}>
                          <div class="hint">{s.error}</div>
                        </Show>
                      </td>
                      <td>
                        {s.status === "ok"
                          ? (r()?.formats ?? []).map((x) => (
                              <span class="chip">{x.toUpperCase()}</span>
                            ))
                          : "—"}
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}

/** Generates a report now: the period when the report has none of its own, and an optional comment. */
function GenerateDialog(props: {
  report: ReportDefinition;
  onClose: () => void;
  onDone: (s: ReportSnapshot) => void;
}) {
  const day = (d: Date) => d.toISOString().slice(0, 10);
  const today = new Date();
  const [from, setFrom] = createSignal(
    day(new Date(today.getTime() - 7 * 86_400_000)),
  );
  const [until, setUntil] = createSignal(day(today));
  const [comment, setComment] = createSignal("");
  const [run, error, busy] = useAction();
  const own = () => props.report.period === "on-generate";
  const path = () =>
    pathTo(siteTree()?.nodes ?? [], props.report.nodeId)
      .map((n) => n.name)
      .join(" / ");
  return (
    <Dialog
      title={t("reports.generateTitle", { name: props.report.name })}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={() =>
              void run(async () => {
                // Dates are whole local days; the end day is included.
                const end = new Date(`${until()}T00:00:00`);
                end.setDate(end.getDate() + 1);
                const s = await api.reports.generate(props.report.id, {
                  ...(own()
                    ? { from: new Date(`${from()}T00:00:00`), until: end }
                    : {}),
                  comment: comment().trim() || undefined,
                });
                props.onDone(s);
                props.onClose();
              })
            }
          >
            {t("reports.generate")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <p class="hint" style={{ margin: 0 }}>
          {path()} · {t(`reports.period.${props.report.period}`)}
        </p>
        <Show when={own()}>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="genFrom">{t("reports.from")}</label>
              <input
                id="genFrom"
                type="date"
                value={from()}
                onInput={(e) => setFrom(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="genTo">{t("reports.until")}</label>
              <input
                id="genTo"
                type="date"
                value={until()}
                onInput={(e) => setUntil(e.currentTarget.value)}
              />
            </div>
          </div>
        </Show>
        <Show when={props.report.askComment}>
          <div class="field">
            <label for="genComment">{t("reports.comment")}</label>
            <textarea
              id="genComment"
              rows={3}
              maxlength={1000}
              value={comment()}
              placeholder={t("reports.commentPlaceholder")}
              onInput={(e) => setComment(e.currentTarget.value)}
            />
          </div>
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}
